import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Menu, UserAvatar } from '@pocketlaw/tetris';
import { useFuse } from 'shared/domains/common-utils/useFuse';
import { UserMenuDocument } from './UserMenu.gql';
import { MenuFooter } from '../MenuFooter';
import { NoResults } from '../NoResults';
export function UserMenu(props) {
    const { property, initialValue = [], onChange, onClear } = props;
    const { label, isMultiSelect } = property;
    const { data, loading, error } = useQuery(UserMenuDocument);
    const { companyUsers = [] } = data || {};
    const [selectedIds, setSelectedIds] = useState(initialValue);
    const [query, setQuery] = useState('');
    const isValidUser = (user) => { var _a; return Boolean((_a = user.user) === null || _a === void 0 ? void 0 : _a.id); };
    const userNameFallback = t({
        message: 'Deleted user',
        comment: 'Fallback name when user does not exist',
    });
    const users = companyUsers.filter(isValidUser).map((user) => {
        var _a, _b, _c;
        return ({
            id: user.user.id,
            name: (_a = user.user.name) !== null && _a !== void 0 ? _a : userNameFallback,
            email: (_c = (_b = user.user) === null || _b === void 0 ? void 0 : _b.email) !== null && _c !== void 0 ? _c : '',
        });
    });
    const { result, search, active } = useFuse(users, {
        keys: ['name', 'email'],
    });
    const sortAlphabetically = (a, b) => a.name.localeCompare(b.name);
    const usersToRender = active ? result : users;
    const selectedUsers = users.filter((user) => initialValue.includes(user.id)).sort(sortAlphabetically);
    const unselectedUsers = usersToRender.filter((user) => !initialValue.includes(user.id)).sort(sortAlphabetically);
    const handleResetToInitialValue = () => {
        setSelectedIds(initialValue);
    };
    const handleSearch = (value) => {
        setQuery(value);
        search(value);
    };
    const handleChange = (value) => {
        const newValues = selectedIds.includes(value) ? selectedIds.filter((id) => id !== value) : [...selectedIds, value];
        setSelectedIds(newValues);
    };
    const handleSubmit = () => {
        if (selectedIds.length === 0) {
            onClear(property);
        }
        else {
            onChange(property, selectedIds);
        }
    };
    const handleClear = () => {
        onClear(property);
    };
    if (error) {
        return null;
    }
    return (<Menu.ItemNested maxWidth="unset" target={label} loading={loading} onClose={handleResetToInitialValue}>
      <Menu.HeaderSearch value={query} onChange={handleSearch} placeholder={t({
            message: 'Find user...',
            comment: 'Bulk action: Metadata - User menu - Find user placeholder',
        })}/>
      {selectedUsers.length > 0 && (<Menu.Label>
          <Trans comment="Bulk action: Metadata - User menu - Selected users section title">Selected</Trans>
        </Menu.Label>)}
      {selectedUsers.map((user) => (<Menu.ItemCheckbox key={user.id} checked={selectedIds.includes(user.id)} onChange={() => handleChange(user.id)} closeOnClick={!isMultiSelect}>
          <UserAvatar size="sm" title={user.name} subtitle={user.email}/>
        </Menu.ItemCheckbox>))}
      {unselectedUsers.length > 0 && (<Menu.Label>
          <Trans comment="Bulk action: Metadata - User menu - Unselected users section title">Users</Trans>
        </Menu.Label>)}
      {unselectedUsers.map((user) => (<Menu.ItemCheckbox key={user.id} checked={selectedIds.includes(user.id)} onChange={() => handleChange(user.id)} closeOnClick={!isMultiSelect}>
          <UserAvatar size="sm" title={user.name} subtitle={user.email}/>
        </Menu.ItemCheckbox>))}
      {usersToRender.length === 0 && (<NoResults>
          <Trans comment="Bulk action: Metadata - User menu - No users found">No users found</Trans>
        </NoResults>)}
      <MenuFooter onSave={handleSubmit} onClear={handleClear}/>
    </Menu.ItemNested>);
}
